import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDashboardViewModel } from '../viewmodels/DashboardViewModel';
import { useSidebar } from '../contexts/SidebarContext';

import { Customer } from '../models/CustomerModels';
import type { DashboardKeywordListItem } from '../models/DashboardModels';
import { CustomerService } from '../services/CustomerService';
import { DashboardService } from '../services/DashboardService';
import { Payment } from '../models/PaymentModels';
import { PaymentService } from '../services/PaymentService';
import { ModernStatsCard } from '../components/ui/ModernStatsCard';
import DashboardCustomerTable from '../components/DashboardCustomerTable'; // 고객 테이블 컴포넌트
import DashboardPaymentTable from '../components/DashboardPaymentTable';

import Calendar from '../components/Calendar';
import PageHeader from '../components/ui/PageHeader';
import KeywordStatusSplitCard from '../components/KeywordStatusSplitCard';
import DashboardKeywordTable from '../components/DashboardKeywordTable';
import { useSelectedMonthPaymentStats } from '../hooks/useSelectedMonthPaymentStats';
import {
  MainContent,
  StatsGrid,
  LoadingSpinner,
  ErrorMessage
} from '../styles/DashboardStyles';
import styled from 'styled-components';
import { colors, spacing, typography, borderRadius, shadows } from '../styles/designTokens';
import WeeklyPaymentPointDeductionChart from '../components/WeeklyPaymentPointDeductionChart';
import Pagination from '../components/Pagination';
import { getDashboardWeekChartAnchorIso } from '../utils/dashboardWeekChartAnchor';

// 헤더 날짜 섹션 스타일
const DateSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
  margin: 0 0 ${spacing[6]} 0;
`;

const DateLabel = styled.span`
  margin-right: ${spacing[2]};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.secondary};
`;

const FullWidthChartsArea = styled.section`
  width: 100%;
  margin-bottom: ${spacing[8]};
`;

const EmbedTableRegion = styled.section`
  margin-top: ${spacing[6]};
  padding-top: ${spacing[6]};
  border-top: 1px solid ${colors.semantic.border.primary};
`;





// 새로운 테이블을 위한 스타일 wrapper - 사이드바 상태에 최적화
/** 차트에서 받은 YYYY-MM-DD → 결제 API year_month(YYYY-MM) */
function isoDateToYearMonth(iso: string): string | null {
  const m = iso.trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1].slice(0, 7) : null;
}

const CARD_KEYWORD_ACTIVE = '키워드 활성';
const CARD_KEYWORD_INACTIVE = '키워드 비활성';

/** 고객·결제·키워드 임베드 목록 — 고객/결제 관리 페이지와 동일한 페이지 크기 */
const DASHBOARD_EMBED_PAGE_SIZE = 8;

type EmbedListPagination = {
  page: number;
  total: number;
  totalPages: number;
  limit: number;
};

function normalizeTotalPages(total: number, limit: number, apiTotalPages?: number): number {
  if (apiTotalPages != null && apiTotalPages > 0) {
    return apiTotalPages;
  }
  if (total <= 0) {
    return 0;
  }
  return Math.max(1, Math.ceil(total / Math.max(1, limit)));
}

const CustomerTableContainer = styled.div<{ $sidebarCollapsed?: boolean }>`
  margin-top: ${spacing[8]};
  background-color: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.sm};
  border: 1px solid ${colors.semantic.border.primary};
  overflow: hidden;

  @media (min-width: 1400px) {
    max-width: ${({ $sidebarCollapsed }) =>
    $sidebarCollapsed ? '1000px' : '1200px'};
    margin: ${spacing[8]} auto 0 auto;
  }
`;

// 대시보드 뷰 컴포넌트
const Dashboard: React.FC = () => {
  const { collapsed } = useSidebar(); // 사이드바 상태 가져오기
  const [activeCard, setActiveCard] = useState<string | null>('현재 고객'); // 기본값을 '현재 고객'으로 설정

  // 고객 목록 상태
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [customersLoading, setCustomersLoading] = useState<boolean>(false);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [customerPagination, setCustomerPagination] = useState<EmbedListPagination | null>(null);

  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState<boolean>(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentPagination, setPaymentPagination] = useState<EmbedListPagination | null>(null);

  const [dashboardKeywords, setDashboardKeywords] = useState<DashboardKeywordListItem[] | null>(null);
  const [dashboardKeywordsLoading, setDashboardKeywordsLoading] = useState(false);
  const [dashboardKeywordsError, setDashboardKeywordsError] = useState<string | null>(null);
  const [keywordPagination, setKeywordPagination] = useState<EmbedListPagination | null>(null);
  /** 차트에서 선택한 일이 속한 달(결제 목록 조회용), 상단 주간 캘린더와 무관 */
  const [chartPaymentYearMonth, setChartPaymentYearMonth] = useState<string | null>(null);
  /** 동일 달 재클릭 시 닫기 판별용(같은 날짜 두 번 클릭 시에만 닫힘) */
  const [chartPaymentAnchorIso, setChartPaymentAnchorIso] = useState<string | null>(null);

  // 선택된 날짜 상태 (기본값: 오늘 날짜)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const {
    stats: monthPaymentStats,
    loading: monthPaymentLoading,
    error: monthPaymentError,
    monthTitleLabel
  } = useSelectedMonthPaymentStats(
    selectedDate,
    activeCard === '차트 결제' ? chartPaymentYearMonth : null
  );

  /** 주간 이동 기준: 주 말일(일요일) — 금주는 오늘로 캡 */
  const weekChartAnchorIso = useMemo(
    () => getDashboardWeekChartAnchorIso(selectedDate, { weekStartsOn: 1 }),
    [selectedDate]
  );

  const chartHighlightedIsoDate =
    activeCard === '차트 결제'
      ? chartPaymentAnchorIso ?? weekChartAnchorIso
      : weekChartAnchorIso;

  // 토큰 테스트 상태 - 주석 처리
  /*
  const [testMessage, setTestMessage] = useState<string>('');
  const [testSuccess, setTestSuccess] = useState<boolean>(false);
  const [testLoading, setTestLoading] = useState<boolean>(false);
  */

  // ViewModel 훅 사용
  const {
    dashboardData,
    loading,
    error,
    handleDateChange: vmHandleDateChange,
    getChangeIndicator,
    formatNumber
  } = useDashboardViewModel();

  // AuthViewModel 훅 사용 - 주석 처리
  // const { refreshToken, adminInfo } = useAuthViewModel();

  // 디버깅용 로그
  useEffect(() => {
    if (dashboardData) {
      console.log('차트 데이터 확인:', {
        keywordStats: dashboardData.keywordStats,
        자동: dashboardData.keywordStats?.자동,
        수동: dashboardData.keywordStats?.수동,
        siteDistribution: dashboardData.siteDistribution,
        자동필터결과: dashboardData.siteDistribution.filter(item => item.site.includes('자동')),
        수동필터결과: dashboardData.siteDistribution.filter(item => item.site.includes('수동'))
      });
    }
  }, [dashboardData]);

  // 날짜 변경 핸들러
  const handleDateChange = (date: Date | null) => { // Date | null 타입 허용
    const toLocalISODateString = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    if (date) { // null 체크
      setSelectedDate(date);
      // UTC 변환으로 날짜가 1일 틀어지는 것을 방지합니다.
      const dateString = toLocalISODateString(date);
      vmHandleDateChange(dateString); // ViewModel에 날짜 전달
      const anchor = getDashboardWeekChartAnchorIso(date, { weekStartsOn: 1 });
      if (activeCard === '차트 결제') {
        setChartPaymentAnchorIso(anchor);
        const ym = isoDateToYearMonth(anchor);
        if (ym) setChartPaymentYearMonth(ym);
        setPaymentPage(1);
      }
    } else {
      // 날짜가 null일 경우의 처리
      setSelectedDate(new Date()); // 오늘 날짜로 기본 설정 또는 다른 로직
      vmHandleDateChange(toLocalISODateString(new Date()));
    }
    // 날짜 변경 시에는 카드 상태와 데이터를 유지 (사용자 경험 개선)
    // setActiveCard(null); // 제거: 날짜 변경 시에도 활성 카드 유지
    // setCustomers(null); // 제거: 날짜 변경 시에도 고객 테이블 유지
    // setCustomersError(null); // 제거: 에러 상태 유지
    // setPayments(null); // 제거: 날짜 변경 시에도 결제 테이블 유지
    // setPaymentsError(null); // 제거: 결제 에러 상태 유지
  };

  // 토큰 갱신 테스트 핸들러 - 주석 처리
  /*
  const handleRefreshTokenTest = async () => {
    setTestLoading(true);
    setTestMessage('');
    
    try {
      const success = await refreshToken();
      if (success) {
        setTestSuccess(true);
        setTestMessage('토큰 갱신 성공! 새로운 토큰으로 업데이트되었습니다.');
      } else {
        setTestSuccess(false);
        setTestMessage('토큰 갱신 실패. 다시 로그인해주세요.');
      }
    } catch (error) {
      setTestSuccess(false);
      setTestMessage('토큰 갱신 중 오류가 발생했습니다.');
    } finally {
      setTestLoading(false);
      
      // 3초 후 메시지 자동 삭제
      setTimeout(() => {
        setTestMessage('');
      }, 3000);
    }
  };
  */

  const loadCustomerPage = useCallback(async (page: number) => {
    setCustomersLoading(true);
    setCustomersError(null);
    try {
      const response = await CustomerService.getCustomers({
        page,
        limit: DASHBOARD_EMBED_PAGE_SIZE
      });
      setCustomers(response.customers);
      const limit = response.limit || DASHBOARD_EMBED_PAGE_SIZE;
      setCustomerPagination({
        page: response.page,
        total: response.total,
        totalPages: normalizeTotalPages(response.total, limit, response.totalPages),
        limit
      });
    } catch (err: any) {
      console.error('Error fetching customers for dashboard:', err);
      setCustomersError(err.message || '고객 목록을 불러오는데 실패했습니다.');
      setCustomers(null);
      setCustomerPagination(null);
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  // 고객 목록 조회 함수 (토글 기능 포함)
  const fetchCustomers = useCallback(async () => {
    if (activeCard === '현재 고객' && customers !== null) {
      setCustomers(null);
      setCustomerPagination(null);
      setActiveCard(null);
      setDashboardKeywords(null);
      setDashboardKeywordsError(null);
      setKeywordPagination(null);
      return;
    }

    setActiveCard('현재 고객');
    setChartPaymentYearMonth(null);
    setChartPaymentAnchorIso(null);
    setPayments(null);
    setPaymentsError(null);
    setPaymentPagination(null);
    setPaymentPage(1);
    setDashboardKeywords(null);
    setDashboardKeywordsError(null);
    setKeywordPagination(null);
    setCustomers(null);
    setCustomerPagination(null);
    await loadCustomerPage(1);
  }, [activeCard, customers, loadCustomerPage]);

  const loadKeywordPage = useCallback(async (variant: 'active' | 'inactive', page: number) => {
    setDashboardKeywordsLoading(true);
    setDashboardKeywordsError(null);
    try {
      const res = await DashboardService.getDashboardKeywords({
        status: variant,
        page,
        limit: DASHBOARD_EMBED_PAGE_SIZE
      });
      setDashboardKeywords(res.items);
      const limit = res.limit || DASHBOARD_EMBED_PAGE_SIZE;
      const total = res.total;
      setKeywordPagination({
        page: res.page,
        total,
        totalPages: normalizeTotalPages(total, limit, res.total_pages),
        limit
      });
    } catch (err: any) {
      setDashboardKeywordsError(err.message || '키워드 목록을 불러오는데 실패했습니다.');
      setDashboardKeywords(null);
      setKeywordPagination(null);
    } finally {
      setDashboardKeywordsLoading(false);
    }
  }, []);

  const fetchDashboardKeywords = useCallback(
    async (variant: 'active' | 'inactive') => {
      const cardLabel = variant === 'active' ? CARD_KEYWORD_ACTIVE : CARD_KEYWORD_INACTIVE;
      if (activeCard === cardLabel && dashboardKeywords !== null) {
        setDashboardKeywords(null);
        setDashboardKeywordsError(null);
        setKeywordPagination(null);
        setActiveCard(null);
        return;
      }
      setActiveCard(cardLabel);
      setChartPaymentYearMonth(null);
      setChartPaymentAnchorIso(null);
      setCustomers(null);
      setCustomersError(null);
      setCustomerPagination(null);
      setPayments(null);
      setPaymentsError(null);
      setPaymentPagination(null);
      setPaymentPage(1);
      await loadKeywordPage(variant, 1);
    },
    [activeCard, dashboardKeywords, loadKeywordPage]
  );

  const handleChartDayClick = useCallback(
    (isoDate: string) => {
      const ym = isoDateToYearMonth(isoDate);
      if (!ym) return;
      const dayKey = isoDate.trim().match(/^(\d{4}-\d{2}-\d{2})/)?.[1] ?? '';
      if (
        activeCard === '차트 결제' &&
        chartPaymentYearMonth === ym &&
        chartPaymentAnchorIso === dayKey
      ) {
        setPayments(null);
        setPaymentsError(null);
        setPaymentPagination(null);
        setPaymentPage(1);
        setActiveCard(null);
        setChartPaymentYearMonth(null);
        setChartPaymentAnchorIso(null);
        setDashboardKeywords(null);
        setDashboardKeywordsError(null);
        setKeywordPagination(null);
        return;
      }
      if (activeCard === '차트 결제' && chartPaymentYearMonth === ym) {
        setChartPaymentAnchorIso(dayKey);
        return;
      }
      setActiveCard('차트 결제');
      setChartPaymentYearMonth(ym);
      setChartPaymentAnchorIso(dayKey);
      setPaymentPage(1);
      setCustomers(null);
      setCustomersError(null);
      setCustomerPagination(null);
      setDashboardKeywords(null);
      setDashboardKeywordsError(null);
      setKeywordPagination(null);
    },
    [activeCard, chartPaymentYearMonth, chartPaymentAnchorIso]
  );

  useEffect(() => {
    if (activeCard !== '차트 결제' || !chartPaymentYearMonth) return;
    let cancelled = false;
    setPaymentsLoading(true);
    setPaymentsError(null);
    setPayments(null);
    PaymentService.getPaymentStatsAndList({
      page: paymentPage,
      limit: DASHBOARD_EMBED_PAGE_SIZE,
      year_month: chartPaymentYearMonth
    })
      .then(res => {
        if (!cancelled) {
          setPayments(res.listResponse.payments);
          const lr = res.listResponse;
          const limit = lr.limit || DASHBOARD_EMBED_PAGE_SIZE;
          setPaymentPagination({
            page: lr.page,
            total: lr.total,
            totalPages: normalizeTotalPages(lr.total, limit, lr.totalPages),
            limit
          });
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setPaymentsError(err.message || '결제 목록을 불러오는데 실패했습니다.');
          setPaymentPagination(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPaymentsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeCard, chartPaymentYearMonth, paymentPage]);

  useEffect(() => {
    if (activeCard === '현재 고객' && customers === null && !customersLoading) {
      void loadCustomerPage(1);
    }
  }, [activeCard, customers, customersLoading, loadCustomerPage]);

  // 로딩 상태 처리
  if (loading) {
    return (
      <MainContent>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </MainContent>
    );
  }

  // 에러 상태 처리
  if (error) {
    return (
      <MainContent>
        <ErrorMessage>{error}</ErrorMessage>
      </MainContent>
    );
  }

  // 데이터가 없는 경우 처리
  if (!dashboardData) {
    return (
      <MainContent>
        <ErrorMessage>데이터를 불러올 수 없습니다.</ErrorMessage>
      </MainContent>
    );
  }

  const { stats, weeklyDeduction } = dashboardData;

  const keywordVariantForPagination: 'active' | 'inactive' | null =
    activeCard === CARD_KEYWORD_ACTIVE
      ? 'active'
      : activeCard === CARD_KEYWORD_INACTIVE
        ? 'inactive'
        : null;

  return (
    <MainContent>
      {/* 페이지 헤더 */}
      <PageHeader
        title="대시보드"
        description="주요 지표 및 최근 활동 개요"
      />

      {/* 날짜 선택 섹션 */}
      <DateSection>
        <DateLabel>조회 기준 주간:</DateLabel>
        <Calendar
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          placeholder="yyyy.mm.dd ~ yyyy.mm.dd"
          maxDate={new Date()} // 오늘 이후 날짜 선택 불가
          mode="week"
          stepDays={7}
          weekStartsOn={1} // 월요일 시작
        />
      </DateSection>



      {/* 주간 결제/포인트 차감 차트 */}
      {weeklyDeduction && (
        <FullWidthChartsArea>
          <WeeklyPaymentPointDeductionChart
            rangeLabel={weeklyDeduction.rangeLabel}
            previousPayments={weeklyDeduction.previousPayments}
            currentPayments={weeklyDeduction.currentPayments}
            previousPointDeductions={weeklyDeduction.previousPointDeductions}
            currentPointDeductions={weeklyDeduction.currentPointDeductions}
            monthlyCumulativePaymentsSeries={weeklyDeduction.monthlyCumulativePaymentsSeries}
            onChartDayClick={handleChartDayClick}
            highlightedIsoDate={chartHighlightedIsoDate}
          />
        </FullWidthChartsArea>
      )}

      {/* 통계 카드 그리드 - 개선된 반응형 레이아웃 */}
      <StatsGrid $sidebarCollapsed={collapsed}>
        <ModernStatsCard
          title="현재 고객"
          value={stats.현재고객수}
          change={getChangeIndicator(stats.금일가입자수)}
          formatNumber={formatNumber}
          clickable={true}
          onClick={fetchCustomers}
          isActive={activeCard === '현재 고객'}
        />
        <KeywordStatusSplitCard
          variant="active"
          autoCount={stats.활성화키워드수_자동}
          manualCount={stats.활성화키워드수_수동}
          formatNumber={formatNumber}
          clickable
          isActive={activeCard === CARD_KEYWORD_ACTIVE}
          onClick={() => fetchDashboardKeywords('active')}
        />
        <KeywordStatusSplitCard
          variant="inactive"
          autoCount={stats.비활성키워드수_자동}
          manualCount={stats.비활성키워드수_수동}
          formatNumber={formatNumber}
          clickable
          isActive={activeCard === CARD_KEYWORD_INACTIVE}
          onClick={() => fetchDashboardKeywords('inactive')}
        />
        <ModernStatsCard
          title={`${monthTitleLabel} 결제 금액`}
          value={monthPaymentLoading ? 0 : monthPaymentStats?.월결제금액 ?? 0}
          change={
            monthPaymentStats && !monthPaymentError
              ? getChangeIndicator(monthPaymentStats.증감금액)
              : undefined
          }
          metaItems={
            monthPaymentError
              ? [{ label: '알림', value: '통계 조회 실패', color: colors.error[600] }]
              : !monthPaymentLoading && monthPaymentStats
                ? [{ label: '결제 건수', value: monthPaymentStats.월결제건수 }]
                : undefined
          }
          formatNumber={formatNumber}
          isActive={activeCard === '차트 결제'}
        />
      </StatsGrid>

      {(activeCard === '현재 고객' ||
        activeCard === '차트 결제' ||
        activeCard === CARD_KEYWORD_ACTIVE ||
        activeCard === CARD_KEYWORD_INACTIVE) && (
        <EmbedTableRegion
          aria-label={
            activeCard === '현재 고객'
              ? '현재 고객 카드와 연결된 요약 목록'
              : activeCard === '차트 결제'
                ? '차트에서 선택한 날짜가 속한 달의 결제 목록'
                : '키워드 현황 카드와 연결된 키워드 목록'
          }
        >
          <CustomerTableContainer $sidebarCollapsed={collapsed}>
            {activeCard === '현재 고객' && customersLoading && <LoadingSpinner />}
            {activeCard === '현재 고객' && customersError && (
              <ErrorMessage>{customersError}</ErrorMessage>
            )}
            {activeCard === '현재 고객' && customers && !customersLoading && !customersError && (
              <DashboardCustomerTable customers={customers} />
            )}
            {activeCard === '현재 고객' &&
              !customersLoading &&
              !customersError &&
              customerPagination &&
              customerPagination.total > 0 && (
                <Pagination
                  currentPage={customerPagination.page}
                  totalPages={customerPagination.totalPages}
                  total={customerPagination.total}
                  limit={customerPagination.limit}
                  onPageChange={page => void loadCustomerPage(page)}
                />
              )}
            {activeCard === '차트 결제' && paymentsLoading && <LoadingSpinner />}
            {activeCard === '차트 결제' && paymentsError && (
              <ErrorMessage>{paymentsError}</ErrorMessage>
            )}
            {activeCard === '차트 결제' && payments && !paymentsLoading && !paymentsError && (
              <DashboardPaymentTable payments={payments} />
            )}
            {activeCard === '차트 결제' &&
              !paymentsLoading &&
              !paymentsError &&
              paymentPagination &&
              paymentPagination.total > 0 && (
                <Pagination
                  currentPage={paymentPagination.page}
                  totalPages={paymentPagination.totalPages}
                  total={paymentPagination.total}
                  limit={paymentPagination.limit}
                  onPageChange={setPaymentPage}
                />
              )}
            {(activeCard === CARD_KEYWORD_ACTIVE || activeCard === CARD_KEYWORD_INACTIVE) &&
              dashboardKeywordsLoading && <LoadingSpinner />}
            {(activeCard === CARD_KEYWORD_ACTIVE || activeCard === CARD_KEYWORD_INACTIVE) &&
              dashboardKeywordsError && <ErrorMessage>{dashboardKeywordsError}</ErrorMessage>}
            {activeCard === CARD_KEYWORD_ACTIVE &&
              dashboardKeywords &&
              !dashboardKeywordsLoading &&
              !dashboardKeywordsError && (
                <DashboardKeywordTable items={dashboardKeywords} variant="active" />
              )}
            {activeCard === CARD_KEYWORD_INACTIVE &&
              dashboardKeywords &&
              !dashboardKeywordsLoading &&
              !dashboardKeywordsError && (
                <DashboardKeywordTable items={dashboardKeywords} variant="inactive" />
              )}
            {keywordVariantForPagination &&
              !dashboardKeywordsLoading &&
              !dashboardKeywordsError &&
              keywordPagination &&
              keywordPagination.total > 0 && (
                <Pagination
                  currentPage={keywordPagination.page}
                  totalPages={keywordPagination.totalPages}
                  total={keywordPagination.total}
                  limit={keywordPagination.limit}
                  onPageChange={page => void loadKeywordPage(keywordVariantForPagination, page)}
                />
              )}
          </CustomerTableContainer>
        </EmbedTableRegion>
      )}

    </MainContent>
  );
};

export default Dashboard; 